import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import {
  ESCALATION_REPORT_TYPE,
  MAX_CODE_ATTEMPTS,
  challengeReportLink,
  clearReportSession,
  fetchReport,
  readReportSession,
  storeReportSession,
  toReportDescriptor,
  verifyReportLinkCode,
  type ReportSession,
  type VerifyOutcome,
} from '@/lib/report-access'
import type { RuntimeConfig } from '@/lib/runtime-config'
import { copy } from './copy'
import { EscalationReportView } from './escalation-report-view'
import { CenteredMessage, RetryScreen } from './screens'
import type { EscalationReport } from './types'

type FlowState =
  | { step: 'challenging' }
  | { step: 'dead' }
  | { step: 'throttled' }
  | { step: 'unreachable' }
  | { step: 'enteringCode'; refusals: number; verifying: boolean }
  | { step: 'loadingReport' }
  | { step: 'ready'; report: EscalationReport }
  | { step: 'unrecognizedScope' }
  | { step: 'reportUnreachable' }

export function ReportAccessFlow({ config, linkToken }: { config: RuntimeConfig; linkToken: string }) {
  const [state, setState] = useState<FlowState>({ step: 'challenging' })
  const [code, setCode] = useState('')
  const started = useRef(false)

  const requestCode = useCallback(async () => {
    setState({ step: 'challenging' })
    setCode('')
    try {
      const outcome = await challengeReportLink(config, linkToken)
      if (outcome.status === 'live') {
        if (outcome.challenge.maskedEmail) {
          toast.success(copy.access.codeSent(outcome.challenge.maskedEmail))
        }
        setState({ step: 'enteringCode', refusals: 0, verifying: false })
      } else {
        setState({ step: outcome.status })
      }
    } catch {
      setState({ step: 'unreachable' })
    }
  }, [config, linkToken])

  // Reporting's 404 covers every token it refuses. Straight after a verify the token is fresh, so a
  // 404 is the report itself; a stored token has most likely outlived its fifteen minutes.
  const showReport = useCallback(
    async (session: ReportSession, justVerified: boolean) => {
      setState({ step: 'loadingReport' })
      try {
        const outcome = await fetchReport(config, session)
        if (outcome.status === 'ready') {
          setState({ step: 'ready', report: outcome.report })
          return
        }
        clearReportSession(linkToken)
        if (justVerified) {
          setState({ step: 'reportUnreachable' })
          return
        }
        toast(copy.access.sessionExpired)
        await requestCode()
      } catch {
        setState({ step: 'reportUnreachable' })
      }
    },
    [config, linkToken, requestCode],
  )

  const start = useCallback(() => {
    const stored = readReportSession(linkToken)
    void (stored ? showReport(stored, false) : requestCode())
  }, [linkToken, requestCode, showReport])

  useEffect(() => {
    if (started.current) return
    started.current = true
    start()
  }, [start])

  async function submitCode(event: FormEvent) {
    event.preventDefault()
    if (state.step !== 'enteringCode') return
    const { refusals } = state
    setState({ step: 'enteringCode', refusals, verifying: true })

    let outcome: VerifyOutcome
    try {
      outcome = await verifyReportLinkCode(config, linkToken, code)
    } catch {
      setState({ step: 'unreachable' })
      return
    }

    if (outcome.status === 'refused') {
      if (refusals + 1 >= MAX_CODE_ATTEMPTS) {
        setState({ step: 'dead' })
        return
      }
      toast.error(copy.access.wrongCode)
      setCode('')
      setState({ step: 'enteringCode', refusals: refusals + 1, verifying: false })
      return
    }

    const descriptor = toReportDescriptor(outcome.access)
    if (!descriptor || descriptor.reportType !== ESCALATION_REPORT_TYPE) {
      setState({ step: 'unrecognizedScope' })
      return
    }

    const session: ReportSession = { accessToken: outcome.access.accessToken, descriptor }
    storeReportSession(linkToken, session)
    await showReport(session, true)
  }

  switch (state.step) {
    case 'dead':
      return <CenteredMessage alert>{copy.access.deadLink}</CenteredMessage>
    case 'unrecognizedScope':
      return <CenteredMessage alert>{copy.access.unrecognizedScope}</CenteredMessage>
    case 'unreachable':
      return <RetryScreen title={copy.access.unreachable} onRetry={start} />
    case 'throttled':
      return <RetryScreen title={copy.access.throttled} onRetry={start} />
    case 'reportUnreachable':
      return <RetryScreen title={copy.access.reportUnreachable} onRetry={start} />
    case 'challenging':
    case 'loadingReport':
      return <CenteredMessage>{copy.access.loading}</CenteredMessage>
    case 'ready':
      return (
        <main className="mx-auto max-w-5xl p-4 sm:p-8">
          <EscalationReportView report={state.report} />
        </main>
      )
    case 'enteringCode':
      return (
        <main className="flex min-h-screen items-center justify-center p-8">
          <form
            aria-label={copy.access.formLabel}
            onSubmit={(event) => void submitCode(event)}
            className="flex w-full max-w-xs flex-col gap-3"
          >
            <label className="flex flex-col gap-1 text-sm font-medium">
              {copy.access.codeLabel}
              <input
                className="rounded border border-gray-300 bg-white p-2 text-center text-lg tracking-widest"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
              />
            </label>
            <button
              type="submit"
              disabled={state.verifying || code.length === 0}
              className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {copy.access.submit}
            </button>
          </form>
        </main>
      )
  }
}
